/** Ключи query на `/cars` — фиксированный порядок для стабильного canonical. */
export const CATALOG_FILTER_KEYS = [
  "brand",
  "model",
  "year",
  "color",
  "engine_volume",
  "body_type",
  "transmission",
  "fuel_type",
  "min_price",
  "max_price",
  "seats",
  "location",
  "is_verified",
  "trip_zone",
] as const;

export type CatalogFilterKey = (typeof CATALOG_FILTER_KEYS)[number];

function firstString(v: string | string[] | undefined): string | undefined {
  if (v === undefined) return undefined;
  const s = Array.isArray(v) ? v[0] : v;
  return typeof s === "string" ? s : undefined;
}

/** Нормализованные активные фильтры из `searchParams` страницы. */
export function pickCatalogFilters(
  raw: Record<string, string | string[] | undefined>
): Partial<Record<CatalogFilterKey, string>> {
  const out: Partial<Record<CatalogFilterKey, string>> = {};
  for (const key of CATALOG_FILTER_KEYS) {
    const s = firstString(raw[key])?.trim();
    if (s) out[key] = s;
  }
  return out;
}

/** Стабильная query-строка (порядок ключей не зависит от браузера). */
export function stableCatalogQueryString(filters: Partial<Record<CatalogFilterKey, string>>): string {
  const parts: string[] = [];
  for (const key of CATALOG_FILTER_KEYS) {
    const v = filters[key];
    if (!v) continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
  }
  return parts.join("&");
}

export function carsCatalogPathname(queryString: string): string {
  return queryString ? `/cars?${queryString}` : "/cars";
}

export type CatalogSeoContext = {
  filters: Partial<Record<CatalogFilterKey, string>>;
  queryString: string;
  pathname: string;
  canonicalUrl: string;
  hasFilters: boolean;
};

export function buildCarsCatalogSeo(
  raw: Record<string, string | string[] | undefined>,
  siteBase: string
): CatalogSeoContext {
  const filters = pickCatalogFilters(raw);
  const queryString = stableCatalogQueryString(filters);
  const pathname = carsCatalogPathname(queryString);
  return {
    filters,
    queryString,
    pathname,
    canonicalUrl: `${siteBase.replace(/\/$/, "")}${pathname}`,
    hasFilters: Object.keys(filters).length > 0,
  };
}

type TCat = (key: string, values?: Record<string, string | number>) => string;
type TFilters = (key: string, values?: Record<string, string | number>) => string;
type TCars = (key: string, values?: Record<string, string | number>) => string;

/** Человекочитаемые фрагменты для title / H1 (порядок как у пользовательских фильтров). */
export function catalogFilterSummaryParts(
  filters: Partial<Record<CatalogFilterKey, string>>,
  tCat: TCat,
  tFilters: TFilters,
  tCars: TCars
): string[] {
  const parts: string[] = [];
  if (filters.brand) parts.push(filters.brand);
  if (filters.model) parts.push(filters.model);
  if (filters.year) parts.push(filters.year);
  if (filters.body_type) parts.push(tCat(`body.${filters.body_type}`));
  if (filters.fuel_type) parts.push(tCat(`fuel.${filters.fuel_type}`));
  if (filters.transmission) parts.push(tCat(`transmission.${filters.transmission}`));
  if (filters.color) parts.push(tCat(`color.${filters.color}`));
  if (filters.seats) parts.push(tCars("metaPartSeats", { count: filters.seats }));
  if (filters.trip_zone === "within") parts.push(tFilters("tripZoneWithin"));
  if (filters.trip_zone === "outside") parts.push(tFilters("tripZoneOutside"));
  if (filters.location) parts.push(filters.location);
  if (filters.is_verified === "true") parts.push(tCars("metaPartVerified"));
  if (filters.min_price || filters.max_price) {
    const min = filters.min_price;
    const max = filters.max_price;
    if (min && max) parts.push(tCars("metaPartPriceRange", { min, max }));
    else if (min) parts.push(tCars("metaPartPriceFrom", { min }));
    else if (max) parts.push(tCars("metaPartPriceTo", { max }));
  }
  if (filters.engine_volume) {
    if (filters.engine_volume === "electric") parts.push(tFilters("engineElectric"));
    else parts.push(tFilters("engineLiters", { vol: filters.engine_volume }));
  }
  return parts;
}
